/* eslint-disable react/prop-types */
import React, { useState, useEffect, useMemo, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";

import { styled } from "@mui/material/styles";

import { useForm, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import PropTypes from "prop-types";

import axios from "axios";

import ReactQuill from "react-quill";
import { Notify } from "notiflix/build/notiflix-notify-aio";
import { Loading, Block  } from "notiflix/build/notiflix-loading-aio";

import Box from "@mui/material/Box";

import CircularProgress from "@mui/material/CircularProgress";

import MDBadge from "components/MDBadge";
import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import ControlledSelect from "components/FormInputs/ControlledSelect";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

import Edit from "@mui/icons-material/Edit";
import MDTypography from "components/MDTypography";
import Tooltip from "@mui/material/Tooltip";
import VisibilityIcon from "@mui/icons-material/Visibility";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import MDAvatar from "components/MDAvatar";
import ControlledTextInput from "components/FormInputs/ControlledTextInput";
import ControlledAutoComplete from "components/FormInputs/ControlledAutoComplete";

import Typography from "@mui/material/Typography";

import MDButton from "components/MDButton";
import { AddBox } from "@mui/icons-material";
import IndeterminateCheckBoxIcon from "@mui/icons-material/IndeterminateCheckBox";
import {
  replaceSingleQuoteToSQLFriendly
} from "utils/helperFunctions";

import { editServerProductQuote } from "../schemas/_model";

import noPreview from "assets/images/no-preview.png";

import { useLazyGetAllCompaniesQuery } from "services/companies";
import { useLazyGetAllBDMQuery } from "services/bdm";
import { useLazyGetResellersByCompanyIdQuery } from "services/resellers";
import {
  useLazyGetAllServerProductsByCountryIdQuery,
  usePutServerProductQuoteMutation
} from "services/products";
import { resetStockId } from "store/entities/stockNotifications";

import { stripHTMLTags, imageSrcDecoder } from "utils/helperFunctions";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

const EditServerProductDialog = ({ initialData, selectedCountry }) => {

  const initialFormState = {
    companyId: 0,
    product_id: 0,
    reseller_id: 0,
    bdm_id: 0,
    estimated_price: 0,
    comments: "",
    status: "PENDING",
  };

  const dispatch = useDispatch();

  const [openEditDialog, setOpenEditDialog] = useState(false);

  const [companyResellers, setCompanyResellers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [bdms, setBdms] = useState([]);
  const [serverProducts, setServerProducts] = useState([]);

  const { control, watch, handleSubmit, setValue, reset, resetField, formState: { errors } } = useForm({
    resolver: yupResolver(
      editServerProductQuote
    ),
    defaultValues: initialFormState,
  });

  const [triggerServerProducts] = useLazyGetAllServerProductsByCountryIdQuery(selectedCountry.id);

  const [putServerProductQuote] = usePutServerProductQuoteMutation();

  const [triggerCompanies] = useLazyGetAllCompaniesQuery();

  const [triggerBDM] = useLazyGetAllBDMQuery();

  const [getResellersByCompanyId] = useLazyGetResellersByCompanyIdQuery();

  const companyId = watch("companyId");
  const previousCompanyIdRef = useRef(initialData.resellerCompany);
  const hasCompanyResellers = Array.isArray(companyResellers) && companyResellers.length > 0;
  const hasServerProducts = Array.isArray(serverProducts) && serverProducts.length > 0;
  const hasBdms = Array.isArray(bdms) && bdms.length > 0;

  const loadData = async () => {
    try {
      Loading.dots({
        svgColor: "rgba(159, 3, 3, 0.8)",
      });
      const [companiesRes, bdmRes] = await Promise.all([
        triggerCompanies(selectedCountry.id),
        triggerBDM(),
      ]);

      setCompanies(Array.isArray(companiesRes?.data) ? companiesRes.data : []);
      setBdms(Array.isArray(bdmRes?.data) ? bdmRes.data : []);

      const [serverProductsRes, resellersRes] = await Promise.all([
        triggerServerProducts(selectedCountry.id),
        getResellersByCompanyId(initialData.resellerCompany),
      ]);

      setServerProducts(Array.isArray(serverProductsRes?.data) ? serverProductsRes.data : []);
      setCompanyResellers(Array.isArray(resellersRes?.data) ? resellersRes.data : []);

      reset({
        companyId: initialData.resellerCompany,
        product_id: initialData.productId,
        reseller_id: initialData.resellerId,
        bdm_id: initialData.bdmId,
        estimated_price: initialData.estimatedPrice,
        comments: initialData.comments,
        status: initialData.status,
      });
    } finally {
      Loading.remove(500);
    }
  };

  const handleClickOpen = async () => {
    setOpenEditDialog(true);
    loadData();
  };

  const handleClose = () => {
    setOpenEditDialog(false);
  };

  const onSubmit = async (data) => {
    // setting up the loader
    Loading.dots({
      svgColor: "rgba(159, 3, 3, 0.8)",
    });
    delete data.companyId
    try {
      const putResponse = await putServerProductQuote({ id: initialData.id, data });
      const responseStatus =
        putResponse?.data?.status || putResponse?.data?.response?.status;

      if (responseStatus === "success") {
        Notify.success("Product quote updated successfully!");
        handleClose();
      } else {
        Notify.failure("Something went wrong. Please try again!");
      }
    } catch (e) {
      Notify.failure("Something went wrong. Please try again!");
    } finally {
      Loading.remove(500);
    }
    
  };

  useEffect(() => {
    if (!openEditDialog || !companyId || companyId === previousCompanyIdRef.current) {
      return;
    }

    previousCompanyIdRef.current = companyId;
    setCompanyResellers([]);
    setValue("reseller_id", null);

    Loading.dots({
      svgColor: "rgba(159, 3, 3, 0.8)",
    });

    getResellersByCompanyId(companyId)
      .then((res) => {
        setCompanyResellers(Array.isArray(res?.data) ? res.data : []);
      })
      .finally(() => {
        Loading.remove(500);
      });
  }, [companyId, openEditDialog, getResellersByCompanyId, setValue]);

  return (
    <>
      <Tooltip title="Edit Product" placement="top">
        <IconButton
          aria-label="view"
          size="small"
          sx={{ ml: 1 }}
          onClick={handleClickOpen}
        >
          <Edit color="warning" fontSize="inherit" />
        </IconButton>
      </Tooltip>
      <BootstrapDialog
        // onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        maxWidth="xl"
        sx={{
          "& .MuiDialog-paper": {
            width: "800px",
          },
        }}
        open={openEditDialog}
      >
        <DialogTitle sx={{ m: 0, p: 2, color: "#3f3f3f" }} id="customized-dialog-title">
          Edit Server Product - <strong>{initialData.name}</strong>
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent className="productEditWrapper" dividers>
          <Box>
          <form>
          <GridContainer>
            <GridItem xs={12} sm={12} md={6} lg={6} xl={6} sx={{mb:2}}>
              <ControlledAutoComplete
                id="companyId"
                name="companyId"
                control={control}
                options={Array.isArray(companies) ? companies : []}
                optionLabelProperty="name"
                label="Company"
              />
            </GridItem>
            <GridItem xs={12} sm={12} md={6} lg={6} xl={6} id="resellerElement" sx={{mb:2}}>
              <ControlledAutoComplete
                id="resellerId"
                name="reseller_id"
                control={control}
                options={Array.isArray(companyResellers) ? companyResellers : []}
                optionLabelProperty="email"
                label="Reseller"
                disabled={!hasCompanyResellers}
              />
            </GridItem>
            <GridItem xs={12} sm={12} md={4} lg={4} xl={4}>
              <ControlledAutoComplete
                id="productId"
                name="product_id"
                control={control}
                options={Array.isArray(serverProducts) ? serverProducts : []}
                optionLabelProperty="name"
                label="Product"
                disabled={!hasServerProducts}
              />
            </GridItem>
            <GridItem xs={12} sm={12} md={4} lg={4} xl={4}>
              <ControlledAutoComplete
                id="bdmId"
                name="bdm_id"
                control={control}
                options={Array.isArray(bdms) ? bdms : []}
                optionLabelProperty="email"
                label="BDM"
                disabled={!hasBdms}
              />
            </GridItem>
            <GridItem xs={12} sm={12} md={4} lg={4} xl={4}>
              <ControlledSelect
                name={"status"}
                control={control}
                label="Status *"
                menu={{
                  id: (prop) => prop,
                  value: (prop) => prop,
                  title: "Status",
                  list: ["PENDING", "CONVERTED", "CANCELLED"],
                }}
                sx={{
                  "& .MuiFormLabel-root": {
                    backgroundColor: "#fff",
                  },
                  "& .MuiInputBase-root": {
                    height: "44px",
                  },
                }}
              />
            </GridItem>
            <GridItem xs={12} sm={12} md={4} lg={4} xl={4}>
              <ControlledTextInput
                name="estimated_price"
                control={control}
                labelText="Estimated Price *"
                formControlProps={{
                  fullWidth: true,
                }}
                inputProps={{
                  type: "number",
                }}
              />
            </GridItem>

            <GridItem xs={12} sm={12} md={12} lg={12} xl={12}>
              <ControlledTextInput
                multiline={true}
                name="comments"
                control={control}
                labelText="Comments*"
                formControlProps={{
                  fullWidth: true,
                }}
              />
            </GridItem>
          </GridContainer>
        </form>
        <DialogActions>
          * Required fields
          <Box sx={{ marginLeft: "auto" }}>
            <MDButton
              color="primary"
              variant="gradient"
              id="submit"
              onClick={handleSubmit(onSubmit)}
            >
              Update Product
            </MDButton>
            <MDButton
              color="secondary"
              variant="gradient"
              onClick={() => handleClose()}
              sx={{ ml: 1 }}
            >
              Cancel
            </MDButton>
          </Box>
        </DialogActions>
          </Box>
        </DialogContent>
      </BootstrapDialog>
    </>
  );
};

EditServerProductDialog.propTypes = {
  // children: PropTypes.node.isRequired, // You can also use PropTypes.element if you want to enforce a single element.
  // productData: PropTypes.object.isRequired,
};

export default EditServerProductDialog;
